import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/lib/subscription";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2, ArrowRight, Tag, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { LUXURY_IMAGES } from "@/lib/constants";

const TIER_META = {
  broker_plan: {
    accent: "#d4a574", icon: null, trial: true,
    features: ["Full US distress-intelligence coverage", "5 team seats", "Lead CRM + Saved Searches", "AI Natural Language Search", "Reports (CSV/Excel/JSON/PDF)", "AI Investment Analysis", "PDF investment memos", "Email alerts"],
  },
  enterprise_plan: {
    accent: "#e8b988", icon: Sparkles, popular: true, trial: true,
    features: ["Everything in Broker", "Unlimited team seats", "API access", "Broker Portal + Deal Room", "Equipment Marketplace", "Dedicated CSM", "Priority AI processing", "Bulk investment underwriter"],
  },
  institutional_plan: {
    accent: "#7c9a85", trial: false,
    features: ["Everything in Enterprise", "Custom data feeds", "SLA guarantees", "On-prem deployment option", "Full white-label", "SSO / SAML", "Compliance controls", "24/7 support"],
  },
};

const fmt = (amount) => `$${(amount / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function PricingPage() {
  const { user } = useAuth();
  const sub = useSubscription();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null); // {valid, percent_off, ...}
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => { api.get("/payments/tiers").then((r) => setTiers(r.data.tiers || [])); }, []);

  const applyPromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromo(null); return; }
    if (!user) { nav("/login"); return; }
    setPromoChecking(true);
    try {
      const r = await api.post("/payments/validate-promo", { code });
      if (r.data.valid) {
        setPromo(r.data);
        toast.success(`Code ${r.data.code} applied${r.data.percent_off ? ` — ${r.data.percent_off}% off` : ""}`);
      } else {
        setPromo(null);
        toast.error("Invalid or expired promo code");
      }
    } catch {
      setPromo(null);
      toast.error("Could not validate code");
    } finally { setPromoChecking(false); }
  };

  const clearPromo = () => { setPromo(null); setPromoCode(""); };

  const startCheckout = async (lookup_key) => {
    if (!user) { nav("/login"); return; }
    setBusy(lookup_key);
    try {
      const body = { lookup_key, origin_url: window.location.origin };
      if (promo?.code) body.promo_code = promo.code;
      const r = await api.post("/payments/checkout", body);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const discountedAmount = (amt) => {
    if (!promo) return amt;
    if (promo.percent_off) return Math.round(amt * (100 - promo.percent_off) / 100);
    if (promo.amount_off) return Math.max(0, amt - promo.amount_off);
    return amt;
  };

  return (
    <div className="min-h-screen bg-[#14100c] text-[#f4ede2]">
      <div className="relative overflow-hidden border-b border-[#33291f]">
        <img src={LUXURY_IMAGES[3]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14100c]/60 via-[#14100c]/85 to-[#14100c]" />
        <div className="relative max-w-6xl mx-auto px-8 py-20 text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-3 font-semibold">Pricing</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight mb-4">Start free. Cancel anytime.</h1>
          <p className="text-[16px] text-[#b8ac96] max-w-2xl mx-auto">7-day free trial on every paid plan. No credit-card charge until day 8. AI-scored opportunities, nationwide map, and Deal Room included.</p>

          <div className="mt-6 flex items-center justify-center gap-6 flex-wrap text-[12px] text-[#b8ac96]">
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#34d399]" /> 7-day free trial</div>
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#34d399]" /> Cancel anytime</div>
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#34d399]" /> No setup fees</div>
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#34d399]" /> Secure Stripe checkout</div>
          </div>

          {sub.tier !== "free" && (
            <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded border border-[#d4a574] text-[13px]">
              <Sparkles size={13} className="text-[#d4a574]" />
              <span>Current plan: <strong className="text-[#d4a574]">{sub.tier.toUpperCase()}</strong> · {sub.status}</span>
            </div>
          )}

          <div className="inline-flex gap-1 mt-6 p-1 rounded-lg border border-[#33291f] bg-[#1c1712]">
            <button onClick={() => setInterval("month")} data-testid="interval-month" className={`px-4 py-1.5 rounded text-[12px] font-semibold uppercase tracking-wider ${interval === "month" ? "bg-[#d4a574] text-[#14100c]" : "text-[#b8ac96]"}`}>Monthly</button>
            <button onClick={() => setInterval("year")} data-testid="interval-year" className={`px-4 py-1.5 rounded text-[12px] font-semibold uppercase tracking-wider ${interval === "year" ? "bg-[#d4a574] text-[#14100c]" : "text-[#b8ac96]"}`}>Yearly <span className="text-[9px] text-[#34d399] ml-1">Save 17%</span></button>
          </div>

          {/* Promo Code Row */}
          <div className="mt-8 max-w-md mx-auto">
            {!promo ? (
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6d5a]" />
                  <input
                    type="text"
                    placeholder="Promo code (optional)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                    data-testid="promo-code-input"
                    className="w-full bg-[#1c1712] border border-[#33291f] rounded pl-9 pr-3 py-2 text-[13px] text-[#f4ede2] uppercase tracking-wider placeholder:text-[#7a6d5a] placeholder:normal-case focus:outline-none focus:border-[#d4a574]"
                  />
                </div>
                <button
                  onClick={applyPromo}
                  disabled={!promoCode.trim() || promoChecking}
                  data-testid="apply-promo-btn"
                  className="px-4 py-2 rounded border border-[#4a3a2b] text-[12px] font-semibold uppercase tracking-wider text-[#d4a574] hover:border-[#d4a574] disabled:opacity-50"
                >
                  {promoChecking ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between px-4 py-2 rounded bg-[#34d399]/10 border border-[#34d399]/40" data-testid="promo-applied">
                <div className="flex items-center gap-2 text-[13px]">
                  <Tag size={14} className="text-[#34d399]" />
                  <span className="font-mono font-semibold text-[#34d399]">{promo.code}</span>
                  <span className="text-[#b8ac96]">— {promo.percent_off ? `${promo.percent_off}% off` : `$${(promo.amount_off / 100).toFixed(0)} off`}{promo.duration === "repeating" ? ` for ${promo.duration_in_months} months` : promo.duration === "forever" ? " forever" : ""}</span>
                </div>
                <button onClick={clearPromo} data-testid="clear-promo-btn" className="text-[#7a6d5a] hover:text-[#f4ede2]"><X size={14} /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-16 grid md:grid-cols-3 gap-5">
        {tiers.map((t) => {
          const meta = TIER_META[t.product_id] || {};
          const price = t.prices.find(p => p.interval === interval) || t.prices.find(p => p.interval === "month") || t.prices[0];
          const currentTier = sub.tier === (t.product_id?.replace("_plan", ""));
          const finalAmount = discountedAmount(price.amount);
          const showTrial = meta.trial && !currentTier;
          return (
            <div key={t.id} data-testid={`tier-${t.product_id}`} className={`relative border rounded-lg p-6 flex flex-col ${meta.popular ? "border-[#d4a574] bg-[#241d16]" : "border-[#33291f] bg-[#1c1712]"}`}>
              {meta.popular && <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[#d4a574] text-[#14100c]">Most Popular</div>}
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: meta.accent }}>{t.name}</div>
              <div className="mb-1">
                {promo && finalAmount !== price.amount ? (
                  <>
                    <span className="font-display text-5xl font-bold" style={{ color: meta.accent }}>{fmt(finalAmount)}</span>
                    <span className="text-[12px] text-[#7a6d5a] ml-1">/{price.interval || "once"}</span>
                    <span className="ml-2 text-[13px] line-through text-[#7a6d5a]">{fmt(price.amount)}</span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-5xl font-bold" style={{ color: meta.accent }}>{fmt(price.amount)}</span>
                    <span className="text-[12px] text-[#7a6d5a] ml-1">/{price.interval || "once"}</span>
                  </>
                )}
              </div>
              {showTrial && (
                <div className="text-[11px] font-semibold text-[#34d399] mb-2">7 days free · then {fmt(finalAmount)}/{price.interval}</div>
              )}
              <p className="text-[12px] text-[#b8ac96] min-h-[36px]">{t.description}</p>
              <ul className="space-y-2 my-6 flex-1">
                {(meta.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] text-[#f4ede2]">
                    <Check size={13} className="mt-1 shrink-0" style={{ color: meta.accent }} /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout(price.lookup_key)}
                disabled={busy === price.lookup_key || currentTier}
                data-testid={`subscribe-${t.product_id}-${price.interval}`}
                className={`w-full py-3 rounded font-semibold text-[13px] flex items-center justify-center gap-2 ${meta.popular ? "bg-[#d4a574] text-[#14100c] hover:bg-[#e8b988]" : "border border-[#4a3a2b] text-[#f4ede2] hover:border-[#d4a574] hover:text-[#d4a574]"} disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {busy === price.lookup_key ? <Loader2 size={14} className="animate-spin" /> : currentTier ? "Current Plan" : <>{showTrial ? "Start Free Trial" : "Subscribe"} <ArrowRight size={14} /></>}
              </button>
              {showTrial && <div className="text-center text-[10px] text-[#7a6d5a] mt-2">No charge for 7 days · Cancel anytime</div>}
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-8 pb-16">
        <h3 className="font-display text-2xl font-bold text-center mb-8">Frequently asked</h3>
        <div className="space-y-3">
          {[
            { q: "Am I charged during the free trial?", a: "No. You add a card at checkout to reserve your seat, but the first charge only happens on day 8. Cancel anytime before then and you owe nothing." },
            { q: "Can I cancel anytime?", a: "Yes. One click in Settings → Manage Billing. No phone calls, no retention emails." },
            { q: "What happens to my data if I cancel?", a: "Your saved searches, CRM leads, and reports stay on your account for 30 days in case you come back." },
            { q: "Do you offer team discounts?", a: "Enterprise includes unlimited seats. Institutional is fully custom — email sales@thecloseout.io." },
            { q: "Is the data real-time?", a: "Foreclosures, sheriff sales, and bankruptcy filings refresh daily. Business closures and WARN notices refresh weekly." },
          ].map((f, i) => (
            <details key={i} className="group border border-[#33291f] rounded-lg bg-[#1c1712] p-4 [transition:border-color_200ms_ease] hover:border-[#4a3a2b]" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer text-[14px] font-semibold text-[#f4ede2] flex justify-between items-center list-none">
                {f.q}
                <span className="text-[#d4a574] group-open:rotate-45 [transition:transform_150ms_ease]">+</span>
              </summary>
              <p className="mt-3 text-[13px] text-[#b8ac96] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 pb-16 text-center">
        <div className="text-[11px] font-mono text-[#7a6d5a]">Test mode · Use card <span className="text-[#d4a574]">4242 4242 4242 4242</span> · any future date · any CVC</div>
      </div>
    </div>
  );
}
